'use client';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import type { InputHTMLAttributes} from 'react';
import { useCallback, useState } from 'react';

import { ArrowRight, ArrowRightSmall, Close, Search, ZoobinLogo, ZoobinWithTextFarsi } from '@zoomit/icons';
import { cn, cssVar, IconButton, useDebounce } from '@zoomit/primitives';
import { InputWithRichPlaceholder, theme } from '@zoomit/zoomit-ds';

import { useDynamicSticky } from '../../hooks/useDynamicSticky';
import { SearchDialogSkeleton } from '../search/SearchDialogSkeleton';
import { SearchInstanceLite } from '../shared/menu/SearchInstanceLite';

const SearchDialog = dynamic(() => import('../search/SearchDialog').then((m) => m.SearchDialog), {
  ssr: false,
  loading: () => <SearchDialogSkeleton />,
});

type NavbarWithCloseProps = {
  title?: string | React.ReactNode;
  onBack?: () => void;
  onClose?: () => void;
};

export const NavbarWithClose = (props: NavbarWithCloseProps) => {
  const { title, onClose, onBack } = props;

  const ref = useDynamicSticky();

  return (
    <NavbarBaseWrapper ref={ref} className="sticky top-0">
      <IconButton onClick={onClose}>
        <Close fill={cssVar(theme.grey1)} size="md" />
      </IconButton>

      <div className="text-nowrap">
        {title ?? <ZoobinLogo width={125} height={27} className="justify-self-center" />}
      </div>

      {onBack && (
        <div
          role="button"
          className="text-subhead text-grey1 flex w-fit cursor-pointer items-center gap-2 justify-self-end"
          onClick={onBack}
        >
          بازگشت
          <ArrowRightSmall size="md" />
        </div>
      )}
    </NavbarBaseWrapper>
  );
};

type NavbarBaseProps = React.ComponentProps<'div'>;
export const NavbarBaseWrapper = (props: NavbarBaseProps) => {
  const { className, children, style, ...rest } = props;

  return (
    <div
      className={cn(
        'border-borderPrimary bg-background2 grid min-h-12 grid-cols-3 items-center border-b px-4',
        className,
      )}
      style={{
        direction: 'ltr',
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
};

export const SearchBar = ({
  className,
  onBack,
  wrapperRef,

  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  onBack?: () => void;
  wrapperRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);

  return (
    <>
      <div className={cn('text-black5 flex items-center gap-4 font-normal', className)} ref={wrapperRef}>
        {onBack && (
          <IconButton onClick={onBack}>
            <ArrowRight />
          </IconButton>
        )}
        <InputWithRichPlaceholder
          style={{ background: 'none' }}
          rightNode={
            <div className="bg-primary3 grid size-6 place-items-center rounded-full">
              <Search fill={cssVar(theme.white)} />
            </div>
          }
          onFocus={() => setShowSearchDialog(true)}
          className="grow"
          variant="fill"
          placeholder={
            <div className="flex gap-2">
              جستجو در
              <ZoobinWithTextFarsi width={60} className="dark:**:[.text]:fill-white" />
            </div>
          }
          {...rest}
        />
      </div>
      {showSearchDialog && <SearchDialog onClose={() => setShowSearchDialog(false)} />}
    </>
  );
};

type SearchBarWithSearchProps = {
  topContent?: React.ReactNode | undefined;
  bottomContent?: React.ReactNode | undefined;
  className?: string;
  margin?: number;
  hideBack?: boolean;
};

export function StickySearchBarWithResult(props: SearchBarWithSearchProps) {
  const [query, setQuery] = useState('');

  const debouncedSearchQuery = useDebounce(query, 300);
  const ref = useDynamicSticky({ disabled: Boolean(query), margin: props.margin ?? 0 });
  const showSearchResult = (debouncedSearchQuery === query && query) || (query && debouncedSearchQuery);

  const router = useRouter();

  const handleBack = useCallback(() => {
    if (query) {
      setQuery('');
    } else if (document.referrer.startsWith(window.location.origin) || !document.referrer) {
      router.back();
    } else {
      router.replace('/');
    }
  }, [query]);

  return (
    <div className={cn('pointer-events-none sticky top-0 z-20', props.className)}>
      <div className="bg-background2 pointer-events-auto relative" ref={ref}>
        {props.topContent}
        <SearchBar
          className="px-4 py-2"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onBack={props.hideBack ? undefined : handleBack}
        />
        {props.bottomContent}
      </div>
      <div
        className={cn(
          'bg-background2 z-0 min-h-screen w-full overflow-hidden px-2',
          'ransition-opacity absolute bottom-0 translate-y-full duration-300',
          showSearchResult ? 'pointer-events-auto' : 'pointer-events-none opacity-0',
        )}
      >
        <SearchInstanceLite searchQuery={debouncedSearchQuery} />
      </div>
    </div>
  );
}
